import React from "react";
import { FaBinoculars, FaDumbbell, FaLaptop, FaCheck } from "react-icons/fa6";
import { MdOutlineWallet, MdFitnessCenter, MdTimeline } from "react-icons/md";
import { HiSparkles, HiChartBar } from "react-icons/hi2";
import { BsArrowRight } from "react-icons/bs";

const trainingSteps = [
    {
        step: 1,
        title: "Consultation & Assessment",
        icon: FaBinoculars,
        color: "from-blue-500 to-cyan-500",
        bgColor: "bg-blue-50",
        borderColor: "border-blue-200",
        description: "Start with a thorough consultation covering your fitness goals, health evaluation, and movement analysis.",
        features: ["Health screening", "Goal setting", "Movement analysis", "Body composition"],
        duration: "45-60 min"
    },
    {
        step: 2,
        title: "Custom Training Plan",
        icon: FaLaptop,
        color: "from-purple-500 to-pink-500",
        bgColor: "bg-purple-50",
        borderColor: "border-purple-200",
        description: "Receive a custom training plan tailored to your assessment results and goals.",
        features: ["Personalized workouts", "Flexible locations", "Progressive overload", "Exercise videos"],
        duration: "Within 24hrs"
    },
    {
        step: 3,
        title: "Personalized Nutrition",
        icon: MdOutlineWallet,
        color: "from-green-500 to-emerald-500",
        bgColor: "bg-green-50",
        borderColor: "border-green-200",
        description: "Get a personalized nutrition plan matching your dietary preferences and restrictions.",
        features: ["Macro calculations", "Meal suggestions", "Local food options", "Flexible dieting"],
        duration: "Within 48hrs"
    },
    {
        step: 4,
        title: "1-on-1 Training Sessions",
        icon: FaDumbbell,
        color: "from-orange-500 to-amber-500",
        bgColor: "bg-orange-50",
        borderColor: "border-orange-200",
        description: "Regular sessions with certified trainers providing form correction and motivation.",
        features: ["Expert guidance", "Form correction", "Real-time feedback", "Motivation"],
        duration: "60 min"
    },
    {
        step: 5,
        title: "Progress Tracking",
        icon: HiChartBar,
        color: "from-rose-500 to-red-500",
        bgColor: "bg-rose-50",
        borderColor: "border-rose-200",
        description: "Track progress with regular assessments. We adapt training for maximum results.",
        features: ["Weekly check-ins", "Body measurements", "Photo progress", "Plan adjustments"],
        duration: "Bi-weekly"
    },
    {
        step: 6,
        title: "Lifestyle Integration",
        icon: HiSparkles,
        color: "from-amber-500 to-yellow-500",
        bgColor: "bg-amber-50",
        borderColor: "border-amber-200",
        description: "Build lasting habits and integrate fitness into your lifestyle with ongoing support.",
        features: ["Habit building", "Independence training", "Long-term support", "Maintenance plans"],
        duration: "Ongoing"
    }
];

/**
 * PersonalTrainerStructure Component
 * SEO-optimized, accessible component displaying the 6-step personal training process
 * All content is server-side rendered and visible to search engine crawlers
 */
export const PersonalTrainerStructure = () => {
    return (
        <section 
            className="py-12 md:py-20 bg-linear-to-b from-white via-gray-50 to-white"
            aria-labelledby="training-structure-heading"
        >
            <div className="max-w-7xl mx-auto px-4 md:px-6">
                {/* Section Header */}
                <header className="text-center mb-12 md:mb-16">
                    <div className="inline-flex items-center gap-2 bg-linear-to-r from-orange-100 to-amber-100 px-4 py-2 rounded-full mb-4">
                        <MdFitnessCenter className="text-orange-500 text-base" aria-hidden="true" />
                        <span className="text-xs md:text-sm font-semibold text-orange-700 uppercase tracking-wide">
                            Your Fitness Journey
                        </span>
                    </div>
                    
                    <h2 
                        id="training-structure-heading" 
                        className="text-2xl md:text-4xl font-bold text-gray-900 mb-4"
                    >
                        Our 6-Step Training Structure
                    </h2>
                    <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                        Experience our comprehensive approach to personalized fitness training with certified professionals.
                    </p>
                </header>

                {/* Training Steps - All visible for SEO */}
                <div className="space-y-6 md:space-y-8">
                    {trainingSteps.map((step, index) => {
                        const Icon = step.icon;
                        return (
                            <article 
                                key={step.step}
                                className={`relative overflow-hidden rounded-2xl md:rounded-3xl ${step.bgColor} border-2 ${step.borderColor} p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow duration-300`}
                                itemScope
                                itemType="https://schema.org/HowToStep"
                            >
                                <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                                    {/* Step Number & Icon */}
                                    <div className="flex items-start gap-4 md:gap-6 shrink-0">
                                        <div className={`w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-linear-to-br ${step.color} flex items-center justify-center shadow-lg shrink-0`}>
                                            <Icon className="text-white text-2xl md:text-3xl" aria-hidden="true" />
                                        </div>
                                        <div className="flex flex-col">
                                            <span 
                                                className={`text-sm md:text-base font-bold uppercase bg-linear-to-r ${step.color} bg-clip-text text-transparent tracking-wide`}
                                                itemProp="position"
                                            >
                                                Step {step.step} of 6
                                            </span>
                                            <h3 
                                                className="text-xl md:text-3xl font-bold text-gray-900 mt-1"
                                                itemProp="name"
                                            >
                                                {step.title}
                                            </h3>
                                        </div>
                                    </div>

                                    {/* Step Content */}
                                    <div className="grow">
                                        <p 
                                            className="text-base md:text-lg text-gray-700 leading-relaxed mb-6"
                                            itemProp="text"
                                        >
                                            {step.description}
                                        </p>

                                        {/* Features List */}
                                        <div className="mb-6">
                                            <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">
                                                What's Included:
                                            </h4>
                                            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {step.features.map((feature, idx) => (
                                                    <li
                                                        key={idx}
                                                        className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-sm"
                                                    >
                                                        <div className={`w-6 h-6 rounded-full bg-linear-to-br ${step.color} flex items-center justify-center shrink-0`}>
                                                            <FaCheck className="text-white text-xs" aria-hidden="true" />
                                                        </div>
                                                        <span className="text-sm md:text-base font-medium text-gray-800">
                                                            {feature}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        {/* Duration */}
                                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm">
                                            <MdTimeline className="text-orange-500 text-lg" aria-hidden="true" />
                                            <span className="text-sm md:text-base font-semibold text-gray-700">
                                                Duration: {step.duration}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Step Number Watermark */}
                                <div 
                                    className="absolute top-4 right-4 md:top-6 md:right-6 text-6xl md:text-8xl font-bold opacity-5 select-none pointer-events-none"
                                    aria-hidden="true"
                                >
                                    {step.step}
                                </div>
                            </article>
                        );
                    })}
                </div>

                {/* Call to Action */}
                <div className="text-center mt-12 md:mt-16">
                    <p className="text-lg md:text-xl text-gray-700 mb-6 font-medium">
                        Ready to start your transformation journey?
                    </p>
                    <button
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        className="inline-flex items-center gap-3 px-8 md:px-10 py-4 md:py-5 bg-linear-to-r from-orange-500 to-amber-500 text-white font-bold text-base md:text-lg rounded-full shadow-lg hover:shadow-xl hover:from-orange-600 hover:to-amber-600 transition-all duration-300 transform hover:scale-105 active:scale-95"
                        aria-label="Start your free consultation - scroll to top"
                    >
                        Start Your Free Consultation
                        <BsArrowRight className="text-xl" aria-hidden="true" />
                    </button>
                    <p className="text-sm text-gray-500 mt-4">
                        No credit card required • Free assessment • Cancel anytime
                    </p>
                </div>

                {/* Structured Data for SEO */}
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            "@context": "https://schema.org",
                            "@type": "HowTo",
                            "name": "Personal Training Process - 6 Step Structure",
                            "description": "Comprehensive 6-step personal training program covering assessment, planning, nutrition, training, tracking, and lifestyle integration",
                            "step": trainingSteps.map(step => ({
                                "@type": "HowToStep",
                                "position": step.step,
                                "name": step.title,
                                "text": step.description,
                                "itemListElement": step.features.map((feature, idx) => ({
                                    "@type": "HowToDirection",
                                    "position": idx + 1,
                                    "text": feature
                                }))
                            }))
                        })
                    }}
                />
            </div>
        </section>
    );
};
